import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { jwtDecode } from "jwt-decode";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import {
  pagePrimaryButtonSx,
  pageFieldSx,
} from "../../styles/pageModernStyles";
import { getUserById } from "../../api/User/userApi";
import {
  cancelSkillAgentTask,
  dispatchSkillAgentTask,
  getSkillAgentTaskById,
  markSkillAgentTaskDone,
  updateSkillAgentTask,
} from "../../api/Skills/skillAgentTasksApi";
import { listSkillFichas } from "../../api/Skills/skillFichasApi";
import {
  TASK_STATUS_LABELS,
  TASK_TYPE_LABELS,
  taskStatusColor,
} from "./skillFichaHelpers";

export default function SkillAgentTaskDetailPage() {
  const { id } = useParams();
  const theme = useTheme();
  const navigate = useNavigate();
  const { contentMargin } = useContext(SidebarContext);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [task, setTask] = useState(null);
  const [fichas, setFichas] = useState([]);
  const [form, setForm] = useState({
    Titulo: "",
    Brief: "",
    Temporada: "",
    TipoTarea: "otro",
    SkillFichaId: "",
    ConstraintsJson: { tipología: "", coste: "", mercado: "", refsAncla: "" },
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }
      const decoded = jwtDecode(token);
      const user = await getUserById(decoded.userId);
      if (!user?.Permiso_TareasAgentes) {
        setForbidden(true);
        return;
      }
      const [row, fichasData] = await Promise.all([
        getSkillAgentTaskById(id),
        listSkillFichas({}),
      ]);
      setTask(row);
      setFichas(Array.isArray(fichasData) ? fichasData : []);
      const c =
        row.ConstraintsJson && typeof row.ConstraintsJson === "object"
          ? row.ConstraintsJson
          : {};
      setForm({
        Titulo: row.Titulo || "",
        Brief: row.Brief || "",
        Temporada: row.Temporada || "",
        TipoTarea: row.TipoTarea || "otro",
        SkillFichaId: row.SkillFichaId ? String(row.SkillFichaId) : "",
        ConstraintsJson: {
          tipología: c.tipología || c.tipologia || "",
          coste: c.coste || "",
          mercado: c.mercado || "",
          refsAncla: c.refsAncla || "",
        },
      });
    } catch (err) {
      console.error(err);
      if (err?.response?.status === 403) setForbidden(true);
      else setError("No se pudo cargar la tarea.");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const updated = await updateSkillAgentTask(id, {
        Titulo: form.Titulo,
        Brief: form.Brief,
        Temporada: form.Temporada,
        TipoTarea: form.TipoTarea,
        SkillFichaId: form.SkillFichaId ? Number(form.SkillFichaId) : undefined,
        ConstraintsJson: form.ConstraintsJson,
      });
      setTask(updated);
      setOkMsg("Tarea guardada.");
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo guardar.");
    } finally {
      setSaving(false);
    }
  };

  const handleDispatch = async (promptOnly = true) => {
    setSaving(true);
    setError("");
    try {
      const res = await dispatchSkillAgentTask(id, { promptOnly });
      setTask(res.task);
      setOkMsg(
        `Encargo preparado${
          res.hasPublishedSkill
            ? ` (skill v${res.task.SkillVersionUsed || "?"})`
            : " (aún sin skill publicado — prompt genérico)"
        }. Copia el prompt abajo y pégalo en Cursor.`,
      );
    } catch (err) {
      setError(err?.response?.data?.error || "No se pudo disparar.");
    } finally {
      setSaving(false);
    }
  };

  const handleDone = async () => {
    setSaving(true);
    try {
      const updated = await markSkillAgentTaskDone(id);
      setTask(updated);
      setOkMsg("Tarea marcada como hecha.");
    } catch (err) {
      setError(err?.response?.data?.error || "Error.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("¿Cancelar esta tarea?")) return;
    setSaving(true);
    try {
      const updated = await cancelSkillAgentTask(id);
      setTask(updated);
    } finally {
      setSaving(false);
    }
  };

  const copyPrompt = async () => {
    if (!task?.PromptSnapshot) return;
    try {
      await navigator.clipboard.writeText(task.PromptSnapshot);
      setOkMsg("Prompt copiado al portapapeles.");
    } catch {
      setError("No se pudo copiar.");
    }
  };

  if (forbidden) {
    return (
      <Box sx={{ ...contentMargin, p: 3 }}>
        <Alert severity="warning">
          Sin permiso Tareas Agentes (<b>Permiso_TareasAgentes</b>).
        </Alert>
      </Box>
    );
  }

  if (loading || !task) {
    return (
      <Box
        sx={{
          ...contentMargin,
          display: "flex",
          justifyContent: "center",
          py: 8,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const readOnly = ["running", "done"].includes(task.Status);

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        minHeight: "100dvh",
        maxWidth: 960,
        px: { xs: 1.5, md: 3 },
        py: 2,
        ...contentMargin,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/skills/tareas")}
          size="small"
        >
          Tareas
        </Button>
        <Typography variant="h5" fontWeight={800} sx={{ flex: 1 }}>
          #{task.id} · {task.Titulo}
        </Typography>
        <Chip
          label={TASK_STATUS_LABELS[task.Status] || task.Status}
          color={taskStatusColor(task.Status)}
        />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {okMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setOkMsg("")}>
          {okMsg}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 2 }}>
        <Stack spacing={1.5}>
          <TextField
            label="Título"
            size="small"
            value={form.Titulo}
            disabled={readOnly}
            onChange={(e) => setForm((p) => ({ ...p, Titulo: e.target.value }))}
            sx={pageFieldSx}
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <FormControl
              size="small"
              sx={{ flex: 1, ...pageFieldSx }}
              disabled={readOnly}
            >
              <InputLabel>Ficha</InputLabel>
              <Select
                label="Ficha"
                value={form.SkillFichaId}
                onChange={(e) =>
                  setForm((p) => ({ ...p, SkillFichaId: e.target.value }))
                }
              >
                <MenuItem value="">—</MenuItem>
                {fichas.map((f) => (
                  <MenuItem key={f.id} value={String(f.id)}>
                    {f.Titulo || f.SkillSlug}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              size="small"
              sx={{ minWidth: 160, ...pageFieldSx }}
              disabled={readOnly}
            >
              <InputLabel>Tipo</InputLabel>
              <Select
                label="Tipo"
                value={form.TipoTarea}
                onChange={(e) =>
                  setForm((p) => ({ ...p, TipoTarea: e.target.value }))
                }
              >
                {Object.entries(TASK_TYPE_LABELS).map(([k, label]) => (
                  <MenuItem key={k} value={k}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Temporada"
              size="small"
              value={form.Temporada}
              disabled={readOnly}
              onChange={(e) =>
                setForm((p) => ({ ...p, Temporada: e.target.value }))
              }
              sx={{ minWidth: 120, ...pageFieldSx }}
            />
          </Stack>
          <TextField
            label="Brief"
            multiline
            minRows={4}
            value={form.Brief}
            disabled={readOnly}
            onChange={(e) => setForm((p) => ({ ...p, Brief: e.target.value }))}
            sx={pageFieldSx}
          />
          <Typography variant="caption" fontWeight={700} color="text.secondary">
            CONSTRAINTS
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            {["tipología", "coste", "mercado", "refsAncla"].map((key) => (
              <TextField
                key={key}
                label={key}
                size="small"
                value={form.ConstraintsJson[key] || ""}
                disabled={readOnly}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    ConstraintsJson: {
                      ...p.ConstraintsJson,
                      [key]: e.target.value,
                    },
                  }))
                }
                sx={{ flex: 1, ...pageFieldSx }}
              />
            ))}
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {!readOnly && (
              <Button
                variant="outlined"
                startIcon={<SaveOutlinedIcon />}
                disabled={saving}
                onClick={handleSave}
              >
                Guardar
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<PlayArrowOutlinedIcon />}
              sx={pagePrimaryButtonSx}
              disabled={saving || task.Status === "done"}
              onClick={() => handleDispatch(true)}
            >
              Preparar encargo
            </Button>
            {task.Status !== "done" && task.Status !== "cancelled" && (
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleOutlineIcon />}
                disabled={saving}
                onClick={handleDone}
              >
                Marcar hecha
              </Button>
            )}
            {task.Status !== "cancelled" && task.Status !== "done" && (
              <Button color="inherit" disabled={saving} onClick={handleCancel}>
                Cancelar
              </Button>
            )}
            {task.DriveFolderUrl && (
              <Button
                startIcon={<OpenInNewOutlinedIcon />}
                href={task.DriveFolderUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Abrir Drive
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 3,
          bgcolor: alpha(theme.palette.background.default, 0.6),
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Typography variant="subtitle2" fontWeight={700}>
            PromptSnapshot
            {task.DispatchMode ? ` · ${task.DispatchMode}` : ""}
            {task.SkillVersionUsed != null
              ? ` · skill v${task.SkillVersionUsed}`
              : ""}
          </Typography>
          {task.PromptSnapshot && (
            <Button
              size="small"
              startIcon={<ContentCopyOutlinedIcon />}
              onClick={copyPrompt}
            >
              Copiar
            </Button>
          )}
        </Stack>
        {task.ResultSummary && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            {task.ResultSummary}
          </Alert>
        )}
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1.5,
            borderRadius: 2,
            bgcolor: alpha("#000", 0.04),
            fontSize: "0.78rem",
            whiteSpace: "pre-wrap",
            maxHeight: 420,
            overflow: "auto",
          }}
        >
          {task.PromptSnapshot ||
            "Aún no disparada. Pulsa Disparar para generar el prompt canónico."}
        </Box>
      </Paper>
    </Box>
  );
}
